import { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Routes } from '@/_shared/constants';

export interface ILink {
  key: string;
  name: string;
  url?: string;
  icon?: string;
  subItems?: ILink[];
}

/**
 * Normalizza un path rimuovendo slash finali (tranne quando path === '/')
 */
const normalizePath = (p?: string) => {
  if (!p) return '';
  if (p === '/') return '/';
  return p.replace(/\/+$/, '');
};

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Ritorna true se `pathname` corrisponde a `url` esattamente o è un sotto-percorso:
 * es: url = /departments => match per /departments e /departments/... ma non per /some-departments
 */
const isPathMatch = (url: string | undefined, pathname: string) => {
  if (!url) return false;
  const nUrl = normalizePath(url);
  const nPath = normalizePath(pathname);

  if (nUrl === '/') {
    return nPath === '/' || nPath.startsWith('/');
  }

  if (nPath === nUrl) return true;
  // verifica che nPath inizi con nUrl + '/' (es /departments/vendite)
  return nPath.startsWith(`${nUrl}/`);
};

/**
 * Match per "key" come segmento: evita match parziali (es. 'port' dentro 'export')
 * Verifica che key sia presente come boundary di path: /.../key/... o .../key
 */
const isKeySegmentMatch = (key: string | undefined, pathname: string) => {
  if (!key) return false;
  const nPath = normalizePath(pathname);
  const re = new RegExp(`(^|/)${escapeRegExp(key)}($|/)`);
  return re.test(nPath);
};

export const useMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [openSubMenus, setOpenSubMenus] = useState<number[]>([]);
  const manualOpenRef = useRef(false);
  const internalNavigationRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const organizationSubRoutes = [normalizePath(Routes.Departments), normalizePath(Routes.People)];

  const myAssessmentsSubRoutes = useMemo(() => [Routes.Assessments], []);

  const isOpen = useCallback(
    (index: number, key?: string, url?: string) => {
      if (manualOpenRef.current) {
        return openSubMenus.includes(index);
      }

      const { pathname } = location;

      if (key === 'companyStructure' && organizationSubRoutes.some(r => isPathMatch(r, pathname))) {
        return true;
      }

      if (key === 'myAssessments' && myAssessmentsSubRoutes.some(r => isPathMatch(r, pathname))) {
        return true;
      }

      if (isPathMatch(url, pathname)) {
        return true;
      }

      if (isKeySegmentMatch(key, pathname)) {
        return true;
      }

      return openSubMenus.includes(index);
    },
    [location, myAssessmentsSubRoutes, openSubMenus, organizationSubRoutes],
  );

  const toggle = useCallback((index: number) => {
    manualOpenRef.current = true;
    setOpenSubMenus(prev => {
      const isCurrentlyOpen = prev.includes(index);
      if (isCurrentlyOpen) {
        // chiudi tutto (comportamento precedente)
        return [];
      }
      // mantieni un solo menu aperto (come prima)
      return [index];
    });
  }, []);

  const hasSubMenu = useCallback(
    (link: ILink) => !!(link.subItems && link.subItems.length > 0),
    [],
  );

  const goTo = useCallback(
    (url?: string, parentIndex?: number, hasSub = false) => {
      if (url) {
        internalNavigationRef.current = true;
        navigate(url);
        if (!hasSub) {
          if (parentIndex !== undefined) {
            manualOpenRef.current = true;
            setOpenSubMenus([parentIndex]);
          } else {
            manualOpenRef.current = false;
            setOpenSubMenus([]);
          }
        }
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (internalNavigationRef.current) {
      internalNavigationRef.current = false;
    } else {
      // Navigazione esterna: resetta stato per usare auto-open basato su pathname
      manualOpenRef.current = false;
      setOpenSubMenus([]);
    }
  }, [location.pathname]);

  return { isOpen, toggle, hasSubMenu, goTo, isPathMatch };
};
